import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, Building2, Users, Image as ImageIcon, Plus, Trash2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { id: 1, label: "Add First Site", icon: Building2 },
  { id: 2, label: "Invite Team", icon: Users },
  { id: 3, label: "Upload Floor Plan", icon: ImageIcon },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [siteForm, setSiteForm] = useState({ name: "", address: "", city: "", state: "Maharashtra", pin: "", total_floors: 1 });
  const [createdSite, setCreatedSite] = useState(null);
  const [invites, setInvites] = useState([{ email: "", name: "", phone: "", role: "viewer" }]);
  const [floorForm, setFloorForm] = useState({ floor_number: 1, name: "Ground Floor", image_url: "" });

  useEffect(() => {
    api.get("/sites").then((r) => { if (r.data.length) { setCreatedSite(r.data[0]); } });
  }, []);

  const saveSite = async () => {
    try {
      const res = await api.post("/sites", siteForm);
      setCreatedSite(res.data);
      toast.success("Site created");
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendInvites = async () => {
    let ok = 0;
    for (const inv of invites) {
      if (!inv.email && !inv.name && !inv.phone) continue;
      try { await api.post("/tenant/invite", inv); ok++; } catch (e) {
        toast.error(`${inv.email || inv.name}: ${typeof e.response?.data?.detail === "string" ? e.response.data.detail : "could not be added"}`);
      }
    }
    if (ok) toast.success(`${ok} invitation(s) sent`);
    setStep(3);
  };

  const saveFloor = async () => {
    try {
      if (!floorForm.image_url) return toast.error("Please paste an image URL");
      await api.post("/floors", { ...floorForm, site_id: createdSite.site_id });
      toast.success("Floor plan added");
      nav("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const skip = () => { if (step < 3) setStep(step + 1); else nav("/dashboard"); };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 grid-bg flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-orange-400" strokeWidth={2.5} />
          <h1 className="text-2xl md:text-3xl font-extrabold">Welcome — Let's set up your workspace</h1>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-emerald-500/20 border-emerald-500" : active ? "f360-gradient border-transparent shadow-lg shadow-orange-500/30" : "bg-gray-50 border-gray-200"}`}>
                    {done ? <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2.5} /> : <Icon className="w-4 h-4 text-gray-900" />}
                  </div>
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Step {s.id}</div>
                    <div className={`text-sm ${active ? "text-gray-900 font-semibold" : "text-gray-500"}`}>{s.label}</div>
                  </div>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? "bg-emerald-500" : "bg-gray-100"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="glass-strong rounded-lg p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Add your first site</h2>
              <p className="text-sm text-gray-500">A "site" is one building or facility you want to monitor.</p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="md:col-span-2"><Label>Site Name *</Label><Input data-testid="onb-site-name" value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div className="md:col-span-2"><Label>Address</Label><Input value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>City</Label><Input value={siteForm.city} onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>State</Label><Input value={siteForm.state} onChange={(e) => setSiteForm({ ...siteForm, state: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>PIN</Label><Input value={siteForm.pin} onChange={(e) => setSiteForm({ ...siteForm, pin: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Floors</Label><Input type="number" value={siteForm.total_floors} onChange={(e) => setSiteForm({ ...siteForm, total_floors: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </div>
              <div className="flex justify-between pt-4">
                <Button variant="ghost" onClick={skip}>Skip for now</Button>
                <Button onClick={saveSite} disabled={!siteForm.name} className="f360-gradient border-0" data-testid="onb-step1-next">Continue <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Invite your team</h2>
              <p className="text-sm text-gray-500">Add team members to manage this site. They'll receive a temporary password (logged to backend console in dev mode).</p>
              <div className="space-y-3">
                {invites.map((inv, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input placeholder="Email" value={inv.email} onChange={(e) => { const c = [...invites]; c[idx].email = e.target.value; setInvites(c); }} className="col-span-4 bg-gray-50 border-gray-200" />
                    <Input placeholder="Full Name" value={inv.name} onChange={(e) => { const c = [...invites]; c[idx].name = e.target.value; setInvites(c); }} className="col-span-3 bg-gray-50 border-gray-200" />
                    <Input type="tel" inputMode="tel" placeholder="Mobile" value={inv.phone} onChange={(e) => { const c = [...invites]; c[idx].phone = e.target.value; setInvites(c); }} className="col-span-2 bg-gray-50 border-gray-200" />
                    <Select value={inv.role} onValueChange={(v) => { const c = [...invites]; c[idx].role = v; setInvites(c); }}>
                      <SelectTrigger className="col-span-2 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="building_manager">Building Manager</SelectItem>
                        <SelectItem value="fire_safety_officer">Fire Officer</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => setInvites(invites.filter((_, i) => i !== idx))} className="col-span-1 text-red-400"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></Button>
                  </div>
                ))}
                <Button variant="ghost" onClick={() => setInvites([...invites, { email: "", name: "", phone: "", role: "viewer" }])} className="text-orange-400"><Plus className="w-4 h-4 mr-1" strokeWidth={2.5} />Add another</Button>
              </div>
              <div className="flex justify-between pt-4">
                <Button variant="ghost" onClick={skip}>Skip</Button>
                <Button onClick={sendInvites} className="f360-gradient border-0">Send Invites <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Upload first floor plan</h2>
              <p className="text-sm text-gray-500">Paste a floor plan image URL. You'll be able to place device pins later.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Floor Number</Label><Input type="number" value={floorForm.floor_number} onChange={(e) => setFloorForm({ ...floorForm, floor_number: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Floor Name</Label><Input value={floorForm.name} onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div className="md:col-span-2"><Label>Image URL *</Label><Input value={floorForm.image_url} onChange={(e) => setFloorForm({ ...floorForm, image_url: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="https://example.com/floor1.png" /></div>
              </div>
              {floorForm.image_url && (
                <div className="rounded-lg border border-gray-200 overflow-hidden max-h-72 bg-black/20"><img src={floorForm.image_url} alt="preview" className="w-full object-contain" /></div>
              )}
              <div className="flex justify-between pt-4">
                <Button variant="ghost" onClick={skip}>Skip</Button>
                <Button onClick={saveFloor} className="f360-gradient border-0">Finish & Go to Dashboard <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
